import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { Gift, Sparkles, Clock, Tag } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Non-sensitive UI flag: prevents the exit-intent popup from re-opening in the same browser tab.
const STORAGE_KEY = "monyx_exit_popup_shown";
const COUPON_CODE = "MONYX10";
const MOBILE_FALLBACK_MS = 45000;
const MOBILE_QUERY = "(max-width: 768px)";
const COUNTDOWN_SECONDS = 600;

/* -------- Subcomponent: capture form -------- */
const PopupOfferForm = ({ form, setForm, loading, onSubmit }) => (
  <div>
    <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-blue-700 text-white p-6 pb-8">
      <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
      <div className="absolute -left-8 -bottom-8 h-32 w-32 rounded-full bg-emerald-300/20 blur-2xl" />
      <div className="relative">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-[11px] font-bold tracking-wide">
          <Sparkles className="h-3.5 w-3.5" /> ESPERA! OFERTA EXCLUSIVA
        </div>
        <DialogHeader className="mt-3">
          <DialogTitle className="font-display text-3xl leading-tight text-white">
            Pegue <span className="underline decoration-emerald-200">10% OFF extra</span> antes de sair
          </DialogTitle>
          <DialogDescription className="text-emerald-50 mt-2">
            Desconto adicional em qualquer plano Monyxx. Válido só nos próximos minutos.
          </DialogDescription>
        </DialogHeader>
      </div>
    </div>

    <form onSubmit={onSubmit} className="p-6 space-y-3">
      <Input
        placeholder="Seu nome"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        data-testid="exit-name-input"
      />
      <Input
        type="email"
        placeholder="Seu melhor e-mail"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        data-testid="exit-email-input"
      />
      <Button
        type="submit"
        disabled={loading}
        className="monyx-btn-primary text-white w-full h-12 rounded-full font-semibold"
        data-testid="exit-submit-btn"
      >
        <Gift className="mr-2 h-4 w-4" />
        {loading ? "Liberando..." : "Quero meu cupom de 10% OFF"}
      </Button>
      <p className="text-[11px] text-slate-500 text-center">
        Sem spam. Você pode sair quando quiser.
      </p>
    </form>
  </div>
);

/* -------- Subcomponent: claimed coupon view -------- */
const PopupCouponView = ({ mm, ss, copied, onCopy, onClose }) => (
  <div className="p-8 text-center">
    <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center">
      <Gift className="h-7 w-7" />
    </div>
    <h3 className="mt-4 font-display text-2xl font-bold">Seu cupom está liberado!</h3>
    <p className="mt-1 text-sm text-slate-600">Use no checkout para ganhar 10% OFF extra.</p>

    <button
      onClick={onCopy}
      className="mt-5 w-full flex items-center justify-between gap-3 px-4 py-4 rounded-xl border-2 border-dashed border-emerald-300 bg-emerald-50 hover:bg-emerald-100 transition"
      data-testid="exit-copy-coupon"
    >
      <div className="flex items-center gap-3">
        <Tag className="h-5 w-5 text-emerald-600" />
        <span className="font-mono text-xl font-bold tracking-wider text-emerald-800">
          {COUPON_CODE}
        </span>
      </div>
      <span className="text-xs font-semibold text-emerald-700">
        {copied ? "COPIADO ✓" : "COPIAR"}
      </span>
    </button>

    <div className="mt-5 flex items-center justify-center gap-2 text-sm text-slate-600">
      <Clock className="h-4 w-4 text-amber-600" />
      <span>Expira em <b className="text-slate-900 font-mono">{mm}:{ss}</b></span>
    </div>

    <a href="#planos">
      <Button
        onClick={onClose}
        className="monyx-btn-primary text-white w-full h-12 rounded-full font-semibold mt-5"
        data-testid="exit-goto-plans"
      >
        Ver planos e usar cupom
      </Button>
    </a>
  </div>
);

/* -------- Main popup -------- */
const ExitIntentPopup = () => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(COUNTDOWN_SECONDS);
  const shownRef = useRef(false);

  const trigger = useCallback(() => {
    if (shownRef.current) return;
    shownRef.current = true;
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(true);
  }, []);

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 0) trigger();
  }, [trigger]);

  // Exit-intent (desktop) + 45s timeout fallback (mobile)
  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY)) return undefined;

    const mobileTimer = setTimeout(() => {
      if (window.matchMedia(MOBILE_QUERY).matches) trigger();
    }, MOBILE_FALLBACK_MS);

    document.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      document.removeEventListener("mouseleave", handleMouseLeave);
      clearTimeout(mobileTimer);
    };
  }, [trigger, handleMouseLeave]);

  // Countdown after claim
  useEffect(() => {
    if (!claimed) return undefined;
    const t = setInterval(() => setTimeLeft((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [claimed]);

  const mm = String(Math.floor(timeLeft / 60)).padStart(2, "0");
  const ss = String(timeLeft % 60).padStart(2, "0");

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Preencha nome e e-mail para liberar o cupom");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, {
        ...form,
        source: "exit-intent",
        plan: `cupom:${COUPON_CODE}`,
      });
      setClaimed(true);
      toast.success("Cupom liberado! Use no checkout.");
    } catch {
      toast.error("Não foi possível liberar agora. Tente de novo.");
    } finally {
      setLoading(false);
    }
  };

  const copyCoupon = async () => {
    try {
      await navigator.clipboard.writeText(COUPON_CODE);
      setCopied(true);
      toast.success("Cupom copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copie manualmente: " + COUPON_CODE);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0"
        data-testid="exit-intent-popup"
      >
        {!claimed ? (
          <PopupOfferForm form={form} setForm={setForm} loading={loading} onSubmit={submit} />
        ) : (
          <PopupCouponView
            mm={mm}
            ss={ss}
            copied={copied}
            onCopy={copyCoupon}
            onClose={() => setOpen(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExitIntentPopup;
